import { useState } from 'react';

interface AllMercedesPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AllMercedesPopup({ isOpen, onClose }: AllMercedesPopupProps) {
  const [showScheduler, setShowScheduler] = useState(false);

  if (!isOpen) return null;

  const handleFindYourMercedes = () => {
    setShowScheduler(true);
  };

  const handleCloseScheduler = () => {
    setShowScheduler(false);
  };

  return (
    <div className="fixed inset-0 bg-black/90 z-50 overflow-y-auto">
      <div className="min-h-screen px-6 py-12">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              All <span className="text-amber-400">Mercedes-Benz</span>
            </h2>
            <button
              onClick={onClose}
              className="w-12 h-12 flex items-center justify-center bg-zinc-800 hover:bg-zinc-700 rounded-full transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-white text-2xl"></i>
            </button>
          </div>

          <div className="bg-zinc-800 rounded-2xl overflow-hidden shadow-2xl mb-8">
            <div className="relative h-96 w-full overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/e677bd58c0e8215c24716aaca4c8fc45.jpg"
                alt="Complete Mercedes-Benz Collection"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-transparent to-transparent"></div>
            </div>
            
            <div className="p-8 md:p-12">
              <div className="mb-8">
                <h3 className="text-3xl font-bold text-white mb-4">
                  Hundreds of Options Available
                </h3>
                <p className="text-gray-300 text-lg leading-relaxed mb-6">
                  Discover our extensive inventory featuring hundreds of new and preowned Mercedes-Benz vehicles. From entry-level luxury to flagship models, we offer unparalleled selection across the entire Mercedes-Benz lineup.
                </p>
                <p className="text-gray-300 text-lg leading-relaxed">
                  Can't find exactly what you're looking for? We provide access to a large order bank, allowing you to custom configure your dream Mercedes-Benz with your preferred options, colors, and packages. Your perfect vehicle is just a conversation away.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-zinc-900 p-6 rounded-lg">
                  <div className="w-12 h-12 flex items-center justify-center bg-amber-400/10 rounded-lg mb-4">
                    <i className="ri-car-line text-amber-400 text-2xl"></i>
                  </div>
                  <h4 className="text-white font-bold text-lg mb-2">New Inventory</h4>
                  <p className="text-gray-400 text-sm mb-3">Latest models with cutting-edge technology and warranty coverage</p>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-amber-400"></i>
                      <span>CLA-Class, C-Class, E-Class, S-Class</span>
                    </li>
                    <li className="flex items-center gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-amber-400"></i>
                      <span>GLA, GLB, GLC, GLE, GLS, G-Class</span>
                    </li>
                    <li className="flex items-center gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-amber-400"></i>
                      <span>Coupes, Convertibles, Roadsters</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-zinc-900 p-6 rounded-lg">
                  <div className="w-12 h-12 flex items-center justify-center bg-amber-400/10 rounded-lg mb-4">
                    <i className="ri-price-tag-3-line text-amber-400 text-2xl"></i>
                  </div>
                  <h4 className="text-white font-bold text-lg mb-2">Preowned Excellence</h4>
                  <p className="text-gray-400 text-sm mb-3">Certified and inspected vehicles offering exceptional value</p>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-amber-400"></i>
                      <span>Mercedes-Benz Certified Pre-Owned</span>
                    </li>
                    <li className="flex items-center gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-amber-400"></i>
                      <span>Comprehensive inspection process</span>
                    </li>
                    <li className="flex items-center gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-amber-400"></i>
                      <span>Extended warranty options</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-gradient-to-r from-amber-400/10 to-amber-400/5 border border-amber-400/20 rounded-lg p-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-amber-400 rounded-lg flex-shrink-0">
                    <i className="ri-settings-3-line text-black text-2xl"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-bold text-lg mb-2">Custom Order Bank Access</h4>
                    <p className="text-gray-300 text-sm">
                      Build your perfect Mercedes-Benz with access to our extensive order bank. Choose your model, select your options, customize colors and packages, and we'll help bring your vision to life. From factory to your driveway.
                    </p>
                  </div>
                </div>
              </div>

              <button 
                onClick={handleFindYourMercedes}
                className="w-full bg-amber-400 text-black font-bold py-4 rounded-lg hover:bg-amber-500 transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Find your Next Mercedes!
              </button>
            </div>
          </div>
        </div>
      </div>

      {showScheduler && (
        <div className="fixed inset-0 bg-black/95 z-[60] overflow-y-auto">
          <div className="min-h-screen px-6 py-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-3xl md:text-4xl font-bold text-white">
                  Find Your <span className="text-amber-400">Mercedes</span>
                </h2>
                <button
                  onClick={handleCloseScheduler}
                  className="w-12 h-12 flex items-center justify-center bg-zinc-800 hover:bg-zinc-700 rounded-full transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-white text-2xl"></i>
                </button>
              </div>

              <div className="bg-zinc-900 rounded-lg shadow-2xl overflow-hidden">
                <div className="aspect-[16/9] w-full">
                  <iframe
                    src="https://mercedeswithjacob.square.site/"
                    className="w-full h-full"
                    style={{ minHeight: '600px' }}
                    title="Find Your Mercedes"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
